import React, { useEffect, useState } from 'react';
import type { FilterSearchPropsMenu } from '@components';
import { FilterSearch } from '@components';
import type { SelectedLeafInfo } from '@components/filter-checkbox-selector/helpers.ts';
import {
    createGenericHandlers,
    getNormalizedFilters,
    getSelectedLeaves,
} from '@components/filter-checkbox-selector/helpers.ts';
import type { TableCell, TableDataDescription } from '@components/table/types.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { TabContent } from '@routes/profile-bar/___pages___/my-profile/styled.tsx';
import { MY_ASSESSMENT_FILTERS_MENU } from '@routes/profile-bar/___pages___/my-results/___shared___/filters.ts';
import type { UserAssessment } from '@routes/profile-bar/___pages___/my-results/api/results.api.ts';
import {
    getAssessmentResults,
    updateAssessmentRequestData,
} from '@routes/profile-bar/___pages___/my-results/model/results.slice.ts';
import {
    TabContentInfoHeader,
    TabContentInfoText,
    TabContentInfoWrapper,
} from '@routes/profile-bar/___shared___/styled.tsx';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { getFiltersFromSearchRequest } from '@utils/commonSearch.ts';
import { totalResults } from '@utils/pagination.ts';

import { PaginatedTable } from './PaginatedTab.tsx';

const MyAssessmentTab: React.FC = () => {
    const { t } = useLingui();
    const NO_RECORDS_HEADER = t`No records found`;
    const NO_ASSESSMENT_HEADER = t`No assessment activities`;
    const NO_RECORDS_SUBTITLE = t`Your search didn't match any results`;
    const NO_ASSESSMENT_SUBTITLE = t`You don't have any assessment activities yet`;

    const [menu, setMenu] = useState<FilterSearchPropsMenu[]>(MY_ASSESSMENT_FILTERS_MENU);
    const [clearAll, setClearAll] = useState(false);
    const dispatch = useAppDispatch();
    const myAssessmentResults = useAppSelector((state) => state.results.myAssessmentResults);
    const requestData = useAppSelector((state) => state.results.myAssessmentRequestData);

    const selectedLeaves: SelectedLeafInfo[] = getSelectedLeaves(menu);
    const filtersCount = selectedLeaves.length + (requestData.textSearch?.trim() !== '' ? 1 : 0);

    const {
        onSearchTermChange,
        onCheckboxSelected,
        onClearAllFilters,
        onClearSearch,
        onSortChange,
        onPaginationChange,
        getActiveFilters,
    } = createGenericHandlers({
        dispatch,
        requestData,
        updateMethod: updateAssessmentRequestData,
        menu,
        setMenu,
        setClearAll,
        defaultMenu: MY_ASSESSMENT_FILTERS_MENU,
        getUpdatedFilters: (selectedLeaves) => ({
            filters: getNormalizedFilters(selectedLeaves),
        }),
    });

    useEffect(() => {
        void dispatch(getAssessmentResults(requestData));
    }, [dispatch, requestData]);

    const userAssessmentTableMapper = (
        userAssessmentPaginated: PaginatedResponse<UserAssessment, EmptyResultsDescription>,
    ): TableDataDescription => {
        const toTableCell = (userLearning: UserAssessment): TableCell[] => [
            { displayName: t`Name`, value: userLearning.name, name: 'name', type: 'text' },
            { displayName: t`Location`, value: userLearning.vessel, name: 'vessel', type: 'text' },
            { displayName: t`Rank (Title)`, value: userLearning.rank, name: 'rank', type: 'text' },
            { displayName: t`Date`, value: userLearning.statusDateUTC, name: 'statusDateUTC', type: 'date' },
            { displayName: t`Status`, value: userLearning.status, name: 'status', type: 'status' },
            { displayName: t`Score`, value: userLearning.scorePercent, name: 'scorePercent', type: 'score' },
            {
                displayName: t`Certificate`,
                value: userLearning.certificateURL,
                name: 'certificateURL',
                type: 'certificate',
            },
        ];
        const headerCells = toTableCell({
            id: 1,
            name: '',
            vessel: '',
            rank: '',
            statusDateUTC: '',
            status: '',
            scorePercent: 0,
            certificateURL: '',
        });
        return {
            rows: userAssessmentPaginated.data.map((u) => ({ key: u.id, cells: toTableCell(u) })),
            headerCells: headerCells,
            currentPage: userAssessmentPaginated.pagination.pageNumber,
            pageSize: userAssessmentPaginated.pagination.pageSize,
            totalItems: totalResults(userAssessmentPaginated),
        };
    };

    return (
        <TabContent>
            <TabContentInfoWrapper>
                <TabContentInfoHeader>{t`My assessment`}</TabContentInfoHeader>
                <TabContentInfoText>{t`Log of activities`}</TabContentInfoText>
            </TabContentInfoWrapper>
            <FilterSearch
                onCheckboxSelected={onCheckboxSelected}
                onChangeSearch={(e) => onSearchTermChange(e.target.value)}
                menu={menu}
                clearAll={clearAll}
                inputValue={requestData.textSearch ?? ''}
                onClearSearch={onClearSearch}
                searchHintText={t`Search by name`}
                onClearAllFilters={onClearAllFilters}
                filtersCount={filtersCount}
                requestData={requestData}
                selectedLeaves={selectedLeaves}
            />
            <PaginatedTable
                data={myAssessmentResults}
                tableDataMapper={userAssessmentTableMapper}
                searchStateTexts={{
                    noFiltersNoDataHeaderText: NO_ASSESSMENT_HEADER,
                    noFiltersNoDataSubtitleText: NO_ASSESSMENT_SUBTITLE,
                    filtersNoDataHeaderText: NO_RECORDS_HEADER,
                    filtersNoDataSubtitleText: NO_RECORDS_SUBTITLE,
                }}
                onSortChange={onSortChange}
                onPaginationChange={onPaginationChange}
                activeFilters={getActiveFilters(getFiltersFromSearchRequest(requestData))}
            />
        </TabContent>
    );
};

export default MyAssessmentTab;
